import React, { useState, useEffect } from 'react';
import { FormAttachment } from '../../../services/formAttachmentService';
import formAttachmentService from '../../../services/formAttachmentService';
import './FormPictures.css';

interface FormPicturesProps {
  formId: string;
  taskIndex?: number;
  taskGroupId?: string; // New prop for task group ID (e.g., "1.0", "1.1")
  showOnlyWhenExpanded?: boolean;
  isExpanded?: boolean;
  /** When set, skips the fetch and renders these attachments (parent loads once). */
  attachmentsOverride?: FormAttachment[];
  /** Omit the block entirely when there are no pictures after loading. */
  hideWhenEmpty?: boolean;
  /** Optional heading override (defaults to "Pictures"). */
  title?: string;
}

const IMAGE_EXT = /\.(jpe?g|png|gif|bmp|webp)$/i;
const PDF_EXT = /\.pdf$/i;

/** True when the attachment can be shown in an <img> / the lightbox. */
export const isImageAttachment = (att: Pick<FormAttachment, 'contentType' | 'filename'>): boolean =>
  (att.contentType || '').toLowerCase().startsWith('image/') || IMAGE_EXT.test(att.filename || '');

export const isPdfAttachment = (att: Pick<FormAttachment, 'contentType' | 'filename'>): boolean =>
  (att.contentType || '').toLowerCase() === 'application/pdf' || PDF_EXT.test(att.filename || '');

const FormPictures: React.FC<FormPicturesProps> = ({ 
  formId, 
  taskIndex, 
  taskGroupId,
  showOnlyWhenExpanded = true,
  isExpanded = false,
  attachmentsOverride,
  hideWhenEmpty = false,
  title = 'Pictures',
}) => {
  const [attachments, setAttachments] = useState<FormAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});

  const [attachmentsByTask, setAttachmentsByTask] = useState<Record<string, FormAttachment[]>>({});

  useEffect(() => {
    console.log('FormPictures useEffect triggered:', {
      formId,
      taskIndex,
      taskGroupId,
      showOnlyWhenExpanded,
      isExpanded,
      shouldLoad: !(showOnlyWhenExpanded && !isExpanded)
    });

    if (showOnlyWhenExpanded && !isExpanded) {
      console.log('FormPictures: Not loading because form is not expanded');
      return; // Don't load if we should only show when expanded and it's not expanded
    }

    if (attachmentsOverride !== undefined) {
      setAttachments(attachmentsOverride);
      setError(null);
      setLoading(false);
      void preloadImages(attachmentsOverride);
      return;
    }

    const loadAttachments = async () => {
      console.log('FormPictures: Starting to load attachments for formId:', formId);
      setLoading(true);
      setError(null);
      
      try {
        let fetchedAttachments: FormAttachment[];
        let fetchedAttachmentsByTask: Record<string, FormAttachment[]>;
        
        if (taskGroupId) {
          // Load attachments for a specific task group
          console.log('FormPictures: Loading attachments for task group:', taskGroupId);
          fetchedAttachmentsByTask = await formAttachmentService.getFormAttachmentsByTask(formId);
          const taskKey = `task-${taskGroupId}`;
          fetchedAttachments = fetchedAttachmentsByTask[taskKey] || [];
          setAttachments(fetchedAttachments);
          
          // Preload images and create blob URLs
          await preloadImages(fetchedAttachments);
        } else if (taskIndex !== undefined) {
          console.log('FormPictures: Loading task attachments for taskIndex:', taskIndex);
          fetchedAttachments = await formAttachmentService.getTaskAttachments(formId, taskIndex);
          setAttachments(fetchedAttachments);
        } else {
          console.log('FormPictures: Loading all form attachments grouped by task');
          fetchedAttachmentsByTask = await formAttachmentService.getFormAttachmentsByTask(formId);
          console.log('FormPictures: Loaded attachments by task:', fetchedAttachmentsByTask);
          setAttachmentsByTask(fetchedAttachmentsByTask);
          
          // Also set all attachments for backward compatibility
          const allAttachments = Object.values(fetchedAttachmentsByTask).flat();
          setAttachments(allAttachments);
        }
      } catch (err) {
        console.error('FormPictures: Error loading form attachments:', err);
        setError('Failed to load pictures');
      } finally {
        setLoading(false);
      }
    };

    loadAttachments();
  }, [formId, taskIndex, taskGroupId, isExpanded, showOnlyWhenExpanded, attachmentsOverride]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach(blobUrl => {
        URL.revokeObjectURL(blobUrl);
      });
    };
  }, [imageBlobUrls]);

  // Preload images and create blob URLs
  const preloadImages = async (attachments: FormAttachment[]) => {
    console.log('FormPictures: Preloading images for', attachments.length, 'attachments');
    const blobUrls: Record<string, string> = {};
    
    for (const attachment of attachments) {
      // PDFs and other files open in a new tab via the download route; no blob needed.
      if (!isImageAttachment(attachment)) continue;
      try {
        const blobUrl = await downloadImageAsBlob(attachment);
        blobUrls[attachment.id.toString()] = blobUrl;
      } catch (error) {
        console.error('FormPictures: Failed to preload image:', attachment.filename, error);
        // Continue with other images even if one fails
      }
    }
    
    setImageBlobUrls(blobUrls);
    console.log('FormPictures: Preloaded', Object.keys(blobUrls).length, 'images');
  };

  // Download image data and create blob URL
  const downloadImageAsBlob = async (attachment: FormAttachment): Promise<string> => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const cleanBaseUrl = baseUrl.replace('/api', '');
      const url = `${cleanBaseUrl}/api/forms/${formId}/attachments/${attachment.id}/download`;
      
      console.log('FormPictures: Downloading image from:', url);
      
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      console.log('FormPictures: Created blob URL for:', attachment.filename);
      return blobUrl;
    } catch (error) {
      console.error('FormPictures: Error downloading image:', attachment.filename, error);
      throw error;
    }
  };

  // Server URL for the attachment (image fallback, and the target for PDFs / other files)
  const getDownloadUrl = (attachment: FormAttachment) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const cleanBaseUrl = baseUrl.replace('/api', '');
    const url = `${cleanBaseUrl}/api/forms/${formId}/attachments/${attachment.id}/download`;
    console.log('FormPictures: Generated attachment URL:', url);
    return url;
  };

  const handleImageClick = (attachment: FormAttachment) => {
    const imageUrl = imageBlobUrls[attachment.id.toString()] || getDownloadUrl(attachment);
    setSelectedImage(imageUrl);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  if (showOnlyWhenExpanded && !isExpanded) {
    return null;
  }

  if (hideWhenEmpty && !loading && !error && attachments.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="form-pictures">
        <h4>{title}</h4>
        <div className="pictures-loading">Loading pictures...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-pictures">
        <h4>{title}</h4>
        <div className="pictures-error">{error}</div>
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className="form-pictures">
        <h4>{title} (0)</h4>
        <div className="pictures-empty">No pictures available</div>
      </div>
    );
  }

  // Render pictures for the current task
  const renderPictures = () => {
    if (attachments.length === 0) {
      return (
        <div className="pictures-empty">
          No pictures available for this task
        </div>
      );
    }

    return (
      <div className="pictures-grid">
        {attachments.map((attachment) => {
          if (!isImageAttachment(attachment)) {
            // PDFs (and any other non-image file): a real link so left-click opens the file
            // in a new tab and right-click offers the browser's "Save link as…".
            const fileUrl = getDownloadUrl(attachment);
            const isPdf = isPdfAttachment(attachment);
            return (
              <div key={attachment.id} className="picture-cell">
                <a
                  className="picture-item picture-file"
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${attachment.filename}`}
                  title={`Open ${attachment.filename} in a new tab`}
                >
                  <div className="picture-file-icon" aria-hidden="true">
                    <i className={isPdf ? 'bi bi-file-earmark-pdf' : 'bi bi-file-earmark'} />
                    <span className="picture-file-kind">{isPdf ? 'PDF' : 'File'}</span>
                  </div>
                  <div className="picture-info">
                    <span className="picture-filename">{attachment.filename}</span>
                  </div>
                </a>
                <a
                  className="picture-download"
                  href={fileUrl}
                  download={attachment.filename}
                  aria-label={`Download ${attachment.filename}`}
                >
                  <i className="bi bi-download" aria-hidden="true" /> Download
                </a>
              </div>
            );
          }

          return (
            <div 
              key={attachment.id} 
              className="picture-item"
              onClick={() => handleImageClick(attachment)}
            >
              <img
                src={imageBlobUrls[attachment.id.toString()] || getDownloadUrl(attachment)}
                alt={attachment.filename}
                className="picture-thumbnail"
                onLoad={() => console.log('FormPictures: Image loaded successfully:', attachment.filename)}
                onError={(e) => console.error('FormPictures: Image failed to load:', attachment.filename, e)}
              />
              <div className="picture-info">
                <span className="picture-filename">{attachment.filename}</span>
                {attachment.imageMetadata && (
                  <span className="picture-metadata">
                    {attachment.imageMetadata.width} × {attachment.imageMetadata.height}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="form-pictures">
      <h4>{title} ({attachments.length})</h4>
      {renderPictures()}

      {/* Image Modal */}
      {selectedImage && (
        <div className="picture-modal" onClick={closeModal}>
          <div className="picture-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="picture-modal-close" onClick={closeModal}>
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full size"
              className="picture-modal-image"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FormPictures;
